import { LocationStrategy } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { DateFilterFn, MatDatepicker } from '@angular/material/datepicker';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { ActivatedRoute } from '@angular/router';
import moment, { Moment } from 'moment';

import { DATE_FORMAT_SAP } from '@shared-constants/date-format-constants';

import { DatahubService } from '../../services/datahub.service';
import { TimeoutService } from '../../services/timeout.service';

@Component({
  selector: 'app-timesheet',
  templateUrl: './timesheet.component.html',
  styleUrl: './timesheet.component.css',
  standalone: false,
})
export class TimesheetComponent implements OnInit {
  public currentStep = 0;
  public timesheetDescription = '';

  public readonly thisWeek: DateFilterFn<Moment | null> = (d: Moment | null): boolean => {
    //Note: This looks a bit weird but it's a property that returns a method which is used in the HTML template which is how the MAT datepicker needs it
    const date = moment(d);
    return this.datahub.date.isSameWeek(date);
  };

  constructor(
    public readonly datahub: DatahubService,
    private readonly activatedRoute: ActivatedRoute,
    private readonly location: LocationStrategy,
    private readonly timeout: TimeoutService,
  ) {
    // In this case onoPopState looks for a user to return to this page via the back button.
    // Angular doesn't run ngOnInit when someone hits back button, so this forces logic to be run
    // On the page when using the back button in order to check if they are on a current or expired contract
    this.location.onPopState(() => {
      this.handleReturnToContract();
    });
  }

  public ngOnInit(): void {
    if (this.datahub.timesheet.isTimesheetInProgress) {
      this.setState();
      return;
    }
    this.activatedRoute.queryParams.subscribe((params) => {
      this.datahub.timesheet.reset();
      const extDocNo = params['number'];
      if (!extDocNo) {
        this.returnToContract();
        return;
      }
      this.updateDate(extDocNo, params['date']);
    });
  }

  public get shouldPreventTabChange(): boolean {
    //Disable the tabs if we have any editable errors for crew or equipment. Any other errors are allowed to continue
    return (
      this.datahub.timesheet.currentEmployeeCrew.some(
        (emp) =>
          (emp.rows.st.hours.errors != null && emp.rows.st.hours.enabled) ||
          (emp.rows.ot.hours.errors != null && emp.rows.ot.hours.enabled) ||
          (emp.rows.dt.hours.errors != null && emp.rows.dt.hours.enabled),
      ) || this.datahub.timesheet.currentEquipmentCrew.some((eqp) => eqp.value.errors != null && eqp.value.enabled)
    );
  }

  public handleDelete(): void {
    const modal = this.datahub.launchResponsiveModal('delete-timesheet', {
      date: this.datahub.date.getSapDateString(true),
    });

    modal.afterClosed().subscribe((shouldDelete) => {
      if (!shouldDelete) return;
      this.datahub.timesheet.deleteDay();
    });
  }

  public handleTabChange(eventProperties: MatTabChangeEvent): void {
    this.datahub.timesheet.currentTab = eventProperties.index;
    this.datahub.timesheet.saveCrew();
  }

  public setDate(picker: MatDatepicker<unknown>): void {
    picker.close();
    this.datahub.timesheet.handleAbandonAttempt$().subscribe((shouldAbandon) => {
      if (!shouldAbandon) {
        this.datahub.date.revert();
        return;
      }
      this.updateDate(this.datahub.extDocument.Documentno, moment(this.datahub.date.selectedDate.value).format(DATE_FORMAT_SAP));
      this.datahub.timesheet.saveCrew();
      this.datahub.date.syncPreviousDate();
      this.datahub.timesheet.reset();
    });
  }

  public handleReturnToTimesheetSelect(): void {
    this.datahub.timesheet.handleAbandonAttempt$().subscribe((shouldAbandon) => {
      if (!shouldAbandon) return;
      this.datahub.timesheet.reset();
      this.returnToContract();
    });
  }

  private updateDate(extDocNo: string, date: string): void {
    this.handleDateFromParams(date);
    this.datahub.timesheet.getForemanTimesheet(extDocNo, date).subscribe(() => {
      this.setState();
    });
    this.timeout.registerTimeout(this.datahub.date.getMoment().clone());
  }

  private handleDateFromParams(userDate: string): void {
    if (!userDate) {
      this.returnToContract();
      return;
    }
    this.datahub.date.setDate(userDate, DATE_FORMAT_SAP);
  }

  private setState(): void {
    this.timesheetDescription = this.datahub.extDocument.display;
    this.currentStep = this.datahub.timesheet.currentTab;
  }

  handleReturnToContract(): void {
    this.datahub.timesheet.handleAbandonAttempt$().subscribe((shouldAbandon) => {
      if (!shouldAbandon) return;
      this.datahub.timesheet.reset();
      this.returnToContract();
    });
  }

  private returnToContract(): void {
    this.datahub.timesheet.reset();
    this.datahub.navigate('/contract');
  }
}
